"use client";

import SelectInput from "@/components/shared/form/SelectInput";
import TextInput from "@/components/shared/form/TextInput";
import PageTitle from "@/components/shared/page-title/PageTitle";
import { useCallback, useEffect, useRef, useState } from "react";
import { commonFetch } from "@/utils/commonFetch";
import { ICollection, ICollectionData } from "@/interface/collection";
import { useData } from "../shared/DataContext";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Card from "../shared/card/Card";
import { useSearchParams } from "next/navigation";
import Image from "next/image";

export default function Collection() {
  const [showMessage, setShowMessage] = useState(false);
  const [showDiv2, setShowDiv2] = useState(false);
  const [overlay, setOverlay] = useState(false);
  const [isSearchMenuOpen, setIsSearchMenuOpen] = useState(false);
  const { state, setSharedData } = useData();
  const [pageIndex, setPageIndex] = useState(1);
  const [artworkTotal, setArtworkTotal] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState({
    artistIds: [],
    searchQuery: null,
    categoryIds: [],
    productionDateFrom: null,
    productionDateTo: null,
  });

  const areAllFiltersEmpty = () => {
    if (
      (selectedFilters?.artistIds?.length === 0 &&
        selectedFilters?.searchQuery === "") ||
      (null &&
        selectedFilters?.categoryIds?.length === 0 &&
        selectedFilters?.productionDateFrom === null &&
        selectedFilters?.productionDateTo === null)
    ) {
      return true;
    } else {
      return false;
    }
  };

  const [artistOptions, setArtistOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [collectionData, setCollectionData] = useState<ICollection | null>(
    null,
  );
  const [hasSortChangedCounter, setHasSortChangedCounter] = useState(0);
  const [filterClickCounter, setFilterClickCounter] = useState(0);
  const [artworkData, setArtworkData] = useState<ICollectionData[]>([]);
  const [sortProperty, setSortProperty] = useState(4);
  const [sortAscending, setSortAscending] = useState(false);
  const hasFetchedData = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(true);
  const [clearClicked, setIsClearClicked] = useState(false);
  const [selectedDateFrom, setSelectedDateFrom] = useState<Date | null>(null);
  const [selectedDateTo, setSelectedDateTo] = useState<Date | null>(null);
  const [bottomPosition, setBottomPosition] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const filterButtonsRef = useRef<HTMLDivElement>(null);
  const options = [
    { value: "A-Z", label: "A-Z" },
    { value: "Z-A", label: "Z-A" },
    { value: "Oldest", label: "Oldest" },
    { value: "Newest", label: "Newest" },
  ];

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowMessage(true);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, []);

  const handleSearchMenuToggle = () => {
    setOverlay((prevIsOpen) => !prevIsOpen);
    setIsSearchMenuOpen((prevIsOpen) => !prevIsOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!filterButtonsRef.current || !ref.current) return;
      
      const isMobile = window.innerWidth < 1280;
      if (!isMobile) {
        // On desktop, use simple check based on page title
        const rect = ref.current.getBoundingClientRect();
        setIsIntersecting(rect.bottom > 200);
        return;
      }
      
      // On mobile, check two conditions:
      // 1. Filter buttons position for collapsing
      // 2. PageTitle position for expanding when scrolling up
      
      const buttonRect = filterButtonsRef.current.getBoundingClientRect();
      const titleRect = ref.current.getBoundingClientRect();
      const collapseThreshold = 0; // Trigger collapse at 280px (more scroll needed)
      
      // If PageTitle is back in view (scrolling up), expand the menu
      if (titleRect.bottom > collapseThreshold) {
        setIsIntersecting(true);
      }
      // If filter buttons go behind header (scrolling down), collapse the menu
      else if (buttonRect.top <= collapseThreshold) {
        setIsIntersecting(false);
      }
    };

    // Run on scroll
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Run once on mount
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    // Show sticky filter menu when the page title is out of view
    // This prevents constant toggling by relying on IntersectionObserver
    setShowDiv2(!isIntersecting);
  }, [isIntersecting]);

  useEffect(() => {
    const storageItem = localStorage.getItem("artworkFilters");
    const fltrs = storageItem && storageItem !== "null" ? JSON.parse(storageItem) : null;

    // Only set filters if they exist and have actual values
    if (fltrs && (
      fltrs.artistIds?.length > 0 ||
      fltrs.searchQuery ||
      fltrs.categoryIds?.length > 0 ||
      fltrs.productionDateFrom ||
      fltrs.productionDateTo
    )) {
      setSelectedFilters(fltrs);
      const dateFrom = fltrs?.productionDateFrom ? new Date(`${fltrs.productionDateFrom}-01-01T02:00:02`) : null;
      const dateTo = fltrs?.productionDateTo ? new Date(`${fltrs.productionDateTo}-01-01T02:00:02`) : null;
      setSelectedDateFrom(dateFrom);
      setSelectedDateTo(dateTo);
    } else {
      // Clear localStorage if filters are empty
      localStorage.removeItem("artworkFilters");
    }

    if (!hasFetchedData.current) {
      hasFetchedData.current = true;
      fetchPageData();
      fetchFilters();
      fetchArtworks();
    }
  }, []);

  const fetchPageData = async () => {
    try {
      const response = await commonFetch.get("collection-page");
      setCollectionData(response);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  const fetchFilters = async () => {
    try {
      const response = await commonFetch.getFromBackend(
        "General/GetFiltersValues",
      );

      setArtistOptions(
        transformSortArray(
          response?.artists.filter((artist: any) => artist.hasArtworks),
        ),
      );
      setCategoryOptions(transformSortArray(response?.categories));
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  const fetchArtworks = async (refetch: boolean = false) => {
    setIsLoading(true);
    const storageItem = localStorage.getItem("artworkFilters");
    const fltrs = storageItem !== null ? JSON.parse(storageItem) : null;

    // Check if filters have actual values
    const hasValidFilters = fltrs && (
      fltrs.artistIds?.length > 0 ||
      fltrs.searchQuery ||
      fltrs.categoryIds?.length > 0 ||
      fltrs.productionDateFrom ||
      fltrs.productionDateTo
    );

    const body = hasValidFilters
      ? {
        pageSize: 24,
        pageIndex: refetch ? 1 : pageIndex,
        filter: {
          ...fltrs,
        },
        sortProperty: sortProperty,
        sortAscending: sortAscending,
      }
      : {
        pageSize: 24,
        pageIndex: refetch ? 1 : pageIndex,
        filter: {
          artistIds: [],
          searchQuery: null,
          categoryIds: [],
          productionDateFrom: null,
          productionDateTo: null,
        },
        sortProperty: sortProperty,
        sortAscending: sortAscending,
      };
    try {
      const response = await commonFetch.postToBackend(
        "Artwork/GetAllArtworks",
        body,
      );
      setArtworkTotal(response?.meta?.pagination?.totalCount);
      if (artworkData?.length > 0 && pageIndex > 1) {
        setArtworkData([...artworkData, ...response?.artworks]);
      } else {
        setArtworkData([...response?.artworks]);
      }
      if (clearClicked) {
        setIsClearClicked(false);
      }
      setHasInitialLoad(true);
    } catch (error) {
      console.error("Error fetching artworks", error);
      setHasInitialLoad(true);
    } finally {
      setIsLoading(false);
    }
  };

  const transformSortArray = (array: any) => {
    let transformedArray = array?.map((item: any) => ({
      value: item.id.toString(),
      label: item.value,
    }));

    return transformedArray;
  };

  const handleOnChooseFilter = (event: any, fieldName: string) => {
    if (event === "") {
      handleOnFilterClick();
    }
    if (event === "clear") {
      setSelectedFilters({
        ...selectedFilters,
        [fieldName]: null,
      });

      if (fieldName === "productionDateFrom" || fieldName === "productionDateTo") {
        const storageItem = localStorage.getItem("artworkFilters");
        const fltrs = storageItem !== null ? JSON.parse(storageItem) : null;
        delete fltrs?.[fieldName];
        localStorage.setItem("artworkFilters", JSON.stringify(fltrs));
        fetchArtworks();
      }


    } else {
      setSelectedFilters({
        ...selectedFilters,
        [fieldName]: event,
      });
    }

    if (fieldName === "productionDateFrom") {
      const fromYear = event;
      const toYear = selectedFilters?.productionDateTo;
      if (fromYear && toYear && fromYear > toYear) {
        setErrorMessage("Date from should be smaller than Date To");
        return;
      }
      setErrorMessage(null);
    }
    if (fieldName === "productionDateTo") {
      const fromYear = selectedFilters?.productionDateFrom;
      const toYear = event;
      if (fromYear && toYear && fromYear > toYear) {
        setErrorMessage("Date from should be smaller than Date To");
        return;
      }
      setErrorMessage(null);
    }
  };

  const handleOnFilterClick = () => {
    // Check if all filters are empty
    const hasAnyFilter =
      selectedFilters?.artistIds?.length > 0 ||
      selectedFilters?.searchQuery ||
      selectedFilters?.categoryIds?.length > 0 ||
      selectedFilters?.productionDateFrom ||
      selectedFilters?.productionDateTo;

    if (hasAnyFilter) {
      localStorage.setItem("artworkFilters", JSON.stringify(selectedFilters));
    } else {
      localStorage.removeItem("artworkFilters");
    }

    setFilterClickCounter(filterClickCounter + 1);
  };

  const handleLoadMore = () => {
    setPageIndex(pageIndex + 1);
  };

  useEffect(() => {
    if (hasSortChangedCounter > 0) {
      setPageIndex(1);
      setTimeout(() => {
        fetchArtworks(true);
      }, 100);
    }
  }, [hasSortChangedCounter]);

  useEffect(() => {
    if (filterClickCounter > 0) {
      fetchArtworks(true);
    }
  }, [filterClickCounter]);

  useEffect(() => {
    const refetch = pageIndex === 1;
    fetchArtworks(refetch);
  }, [pageIndex]);

  const handleSelectCategory = (value: any) => {
    const categoryIds: string[] = Array.isArray(selectedFilters?.categoryIds)
      ? selectedFilters.categoryIds
      : [];
    if (Array.isArray(value) && value?.length === 0) {
      setSelectedFilters((prevFilters: any) => ({
        ...prevFilters,
        categoryIds: [],
      }));
      const storageItem = localStorage.getItem("artworkFilters");
      const fltrs = storageItem !== null ? JSON.parse(storageItem) : null;
      delete fltrs?.categoryIds;
      localStorage.setItem("artworkFilters", JSON.stringify(fltrs));
      fetchArtworks();
    } else {
      const newValue: string[] = Array.isArray(value) ? value : [value];
      const newCategoryIds: string[] = categoryIds
        .filter((id) => !newValue.includes(id))
        .concat(newValue.filter((id) => !categoryIds.includes(id)));
      setSelectedFilters((prevFilters: any) => ({
        ...prevFilters,
        categoryIds: newCategoryIds,
      }));
    }
  };

  const handleSelectAritst = (value: any) => {
    const artistIds: string[] = Array.isArray(selectedFilters?.artistIds)
      ? selectedFilters.artistIds
      : [];
    if (Array.isArray(value) && value?.length === 0) {
      setSelectedFilters((prevFilters: any) => ({
        ...prevFilters,
        artistIds: [],
      }));
      const storageItem = localStorage.getItem("artworkFilters");
      const fltrs = storageItem !== null ? JSON.parse(storageItem) : null;
      delete fltrs?.artistIds;
      localStorage.setItem("artworkFilters", JSON.stringify(fltrs));
      fetchArtworks();
    } else {
      const newValue: string[] = Array.isArray(value) ? value : [value];
      const newArtistIds: string[] = artistIds
        .filter((id) => !newValue.includes(id))
        .concat(newValue.filter((id) => !artistIds.includes(id)));
      setSelectedFilters((prevFilters: any) => ({
        ...prevFilters,
        artistIds: newArtistIds,
      }));
    }
  };

  const handleSelectSort = (event: any) => {
    if (event === "A-Z") {
      setSortProperty(2);
      setSortAscending(true);
    } else if (event === "Z-A") {
      setSortProperty(2);
      setSortAscending(false);
    } else if (event === "Oldest") {
      setSortProperty(4);
      setSortAscending(true);
    } else if (event === "Newest") {
      setSortProperty(4);
      setSortAscending(false);
    }
    setHasSortChangedCounter(hasSortChangedCounter + 1);
  };

  const handleClearComplete = () => {
    setIsClearClicked(false);
  };

  useEffect(() => {
    if (clearClicked) {

      fetchArtworks(true);
    }
  }, [clearClicked]);

  const clearAll = () => {
    localStorage.removeItem("artworkFilters");
    setPageIndex(1);
    setIsClearClicked(true);
    setSelectedDateFrom(null);
    setSelectedDateTo(null);
    setSelectedFilters({
      artistIds: [],
      searchQuery: null,
      categoryIds: [],
      productionDateFrom: null,
      productionDateTo: null,
    });
    // Trigger immediate fetch with no filters
    setTimeout(() => {
      fetchArtworks(true);
    }, 0);
  };

  const receiveDataFromChild = (data: any) => {
    if (data) {
      setFilterClickCounter(0);
      setIsClearClicked(true);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const scrollThreshold = 11000;

      if (scrollY > scrollThreshold) {
        setBottomPosition(true);
      } else setBottomPosition(false);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <div
        id="overlay"
        className={`${showDiv2 && overlay ? "overlay z-[10] md:hidden" : "hidden"
          }`}
      ></div>

      <div className="xl:mt-48 md:mt-40 mt-36">
        <div className="grid xl:grid-cols-12">
          <div
            className={`col-span-10 col-start-1 md:col-start-2 xl:mb-16  ${showDiv2 ? "" : "-mt-[35px]"
              } `}
          >
            <div ref={ref}>
              <PageTitle
                title={collectionData?.pagetitle?.title}
                description={collectionData?.pagetitle?.description}
                cls={"md:ml-[40px] lg:ml-0"}
              ></PageTitle>
            </div>
          </div>
        </div>

        {collectionData && (
          <div
            className={`bg-white pb-4 w-full z-10 block ${showDiv2
              ? isSearchMenuOpen
                ? "fixed md:top-[100px] top-[110px] pt-10 border-b border-[#CECECE] overflow-y-auto xl:overflow-y-visible"
                : "fixed md:top-[100px] top-[110px] pt-10 hidden xl:block border-b border-[#CECECE]"
              : "pb-4"
              }`}
          >
            <div className="xl:flex xl:justify-between w-[90%] xl:w-[95%] mx-auto flex-col md:flex-row mt-[10px]">
              <div className="w-full xl:w-[20%] h-16 relative mb-7 xl:mb-0">
                <TextInput
                  clearClicked={clearClicked}
                  handleClearComplete={handleClearComplete}
                  width={"w-full"}
                  label={"Search"}
                  cls={"pr-10"}
                  placeholder={"Artwork name, artists..."}
                  isSearch={true}
                  onSearchClick={handleOnFilterClick}
                  onChange={(e: any) => {
                    handleOnChooseFilter(e?.target?.value, "searchQuery");
                  }}
                  initialValue={selectedFilters?.searchQuery}
                />
              </div>

              <div></div>
              <div className="w-full xl:w-[65%] xl:flex justify-center flex-col md:flex-row mb-4 xl:mb-0">
                <div className="w-full xl:w-[27%] h-16 relative mb-4 lg:mb-0">
                  <SelectInput
                    sendDataToParent={receiveDataFromChild}
                    clearClicked={clearClicked}
                    handleClearComplete={handleClearComplete}
                    label={"Filter By"}
                    placeholder={"Artists"}
                    width={"100%"}
                    isMultiple={true}
                    isNoBorderRight={true}
                    options={artistOptions}
                    initialSelected={selectedFilters?.artistIds}
                    onSelect={handleSelectAritst}
                  />
                </div>

                <div className="w-full flex justify-between mt-[28px] xl:w-[26%] -mb-[20px] md:mb-0 collection-date relative">
                  <div className="w-full md:w-[50%] h-16 relative mb-4 md:mb-0 ml-0 mr-[20px] xl:mr-0">
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        views={["year"]}
                        label="From"
                        closeOnSelect
                        slotProps={{
                          actionBar: {
                            actions: ["clear"],
                          },
                        }}
                        value={selectedDateFrom}
                        onChange={(e) => {
                          handleOnChooseFilter(
                            e ? new Date(e).getFullYear() : "clear",
                            "productionDateFrom",
                          );
                          setSelectedDateFrom(e);
                        }}
                        className={`w-full xl:[&>div]:!pr-[20px] [&>div]:border [&>div]:border-black xl:[&>div]:border-r-0 [&>div]:border-t-1 [&>div]:border-b-1 [&>div]:rounded-none [&>div]:h-[50px]
                    ${errorMessage
                            ? "[&>div]:border-2  [&>div]:border-l-2  [&>div]:border-red-500"
                            : ""
                          }`}
                      />
                    </LocalizationProvider>
                  </div>
                  <div className="w-full md:w-[50%] h-16 relative mb-4 md:mb-0 lg:ml-0 ml-[25px] lg:mr-0">
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        views={["year"]}
                        label="To"
                        closeOnSelect
                        slotProps={{
                          actionBar: {
                            actions: ["clear"],
                          },
                        }}
                        value={selectedDateTo}
                        onChange={(e) => {
                          handleOnChooseFilter(
                            e ? new Date(e).getFullYear() : "clear",
                            "productionDateTo",
                          );
                          setSelectedDateTo(e);
                        }}
                        className={`w-full xl:[&>div]:!pr-[35px] [&>div]:border [&>div]:border-black  [&>div]:border-t-1 [&>div]:border-b-1 [&>div]:rounded-none [&>div]:h-[50px]
                       ${errorMessage
                            ? "[&>div]:border-2  [&>div]:border-r-2  [&>div]:border-red-500"
                            : " xl:[&>div]:border-r-0"
                          }`}
                      />
                    </LocalizationProvider>
                  </div>
                  {errorMessage && (
                    <p className="text-center text-red-500 bdy-txt-3 absolute top-[50px] whitespace-nowrap">
                      {errorMessage}
                    </p>
                  )}
                </div>

                <div className="w-full xl:w-[27%] xl:mt-5 xl:-ml-4  h-16 relative mb-4 xl:mb-0">
                  <SelectInput
                    clearClicked={clearClicked}
                    handleClearComplete={handleClearComplete}
                    placeholder={"Category"}
                    width={"100%"}
                    cls={`${errorMessage
                      ? " sm:border-l-2  sm:border-l-red-500"
                      : ""
                      }`}
                    isMultiple={true}
                    options={categoryOptions}
                    onSelect={handleSelectCategory}
                    initialSelected={selectedFilters?.categoryIds}
                  />
                </div>

                <div className="w-full xl:w-[12%] h-16 relative mb-7 xl:mb-0 lg:block xl:mt-0 -mt-[35px] !flex" ref={filterButtonsRef} >
                  <button
                    className="btn-primary h-[50px] mt-4 mt-7 xl:w-full w-[50%] mr-[50px] xl:mr-0"
                    onClick={() => {
                      handleOnFilterClick();
                      setIsSearchMenuOpen(false);
                      setOverlay(false);
                    }}
                  >
                    Filter
                  </button>

                  <button
                    onClick={() => {
                      clearAll();
                    }}
                    title="Clear all"
                    className="mt-[28px] w-[45px] pl-[10px] pr-[5px] -mr-[15px] bg-transparent border border-solid border-black h-[50px] py-1.5 items-center justify-between xl:flex hidden"
                  >
                    <Image
                      src="/assets/icons/close.svg"
                      alt="clear"
                      width={15}
                      height={20}
                      className="cursor-pointer"
                    />
                  </button>
                  <button
                    onClick={() => {
                      clearAll();
                    }}
                    className="mt-[28px] w-[50%] bg-transparent border border-solid border-black h-[50px] py-1.5 items-center justify-between xl:hidden block"
                  >
                    Clear all
                  </button>
                </div>

                <div className="w-full xl:w-[15%] h-16 relative lg:hidden">
                  <SelectInput
                    label={"Sort By"}
                    placeholder={"Newest"}
                    width={"100%"}
                    isMultiple={false}
                    options={options}
                    onSelect={handleSelectSort}
                  />
                </div>
              </div>
              <div className="w-full xl:w-[10%] h-16 relative mb-4 lg:mb-0 hidden">
                <button
                  className="btn-primary h-[50px] mt-4 w-full mt-7"
                  onClick={handleOnFilterClick}
                >
                  Filter
                </button>
              </div>

              <div className="w-full xl:w-[15%] h-16 relative hidden lg:block mb-4">
                <SelectInput
                  label={"Sort By"}
                  placeholder={"Newest"}
                  width={"100%"}
                  options={options}
                  isMultiple={false}
                  onSelect={handleSelectSort}
                />
              </div>
            </div>
          </div>
        )}

        {state.sharedData ? null : (
          <button
            className={`btn-filter fixed top-[101px] w-full z-10 block xl:hidden border border-x-0 border-b border-[#CECECE] ${isSearchMenuOpen ? "text-right border-b-0 h-[50px]" : "h-[60px]"
              } ${showDiv2 ? "block" : "hidden"}`}
            onClick={handleSearchMenuToggle}
          >
            {isSearchMenuOpen ? <span>Close</span> : <span>Filter</span>}
          </button>
        )}
      </div>

      {filterClickCounter > 0 && !areAllFiltersEmpty() && (
        <div className="md:ml-10 ml-6 mb-10">
          <p className="card-title">
            Showing <span className="text-[#015549]">(</span>
            <span className="text-[#015549]">{artworkTotal}</span>
            <span className="text-[#015549]">)</span> results
          </p>
        </div>
      )}

      <div
        className={`grid grid-cols-12 md:gap-y-4 xl:gap-y-14 md:gap-10 gap-4 xl:ml-[40px] xl:mr-[40px] lg:ml-[55px] lg:mr-[55px] md:ml-[40px] md:mr-[40px] ml-[18px] mr-[18px]  ${showDiv2 ? "mt-80 md:mt-0" : ""
          } mb-16`}
      >
        {isLoading && artworkData?.length === 0 ? (
          // Show shimmer effect when loading for the first time or searching
          Array.from({ length: 12 }).map((_, index) => (
            <div
              key={index}
              className="col col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 mb-8"
            >
              <ShimmerCard />
            </div>
          ))
        ) : (
          artworkData?.length > 0 &&
          artworkData?.map((artwork, index) => (artwork?.imageReference?.length > 0 && <ArtWorkData artwork={artwork} key={index} />
          ))
        )}
      </div>
      {artworkData?.length < artworkTotal && (
        <div className="load-more text-center mb-8">
          <button
            className="btn-primary w-[150px] h-[50px]"
            onClick={handleLoadMore}
          >
            Load more
          </button>
        </div>
      )}

      {!isLoading && hasInitialLoad && artworkTotal == 0 && artworkData?.length === 0 && (
        <div className="text-center mb-20 no-entries-collection">
          <h3>No entries found</h3>
        </div>
      )}

      {showDiv2 && (
        <div
          className={`fixed ${bottomPosition ? "bottom-[25%]" : "bottom-[7%]"
            }  right-[20px] z-[100] cursor-pointer md:hidden`}
        >
          <Image
            src="/assets/icons/scroll-top.svg"
            alt="scroll"
            width={45}
            height={45}
            className="cursor-pointer"
            onClick={() => {
              window.scrollTo({
                top: 60,
                behavior: "smooth",
              });
            }}
          />
        </div>
      )}
    </>
  );
}

// Shimmer loading component
const ShimmerCard = () => {
  return (
    <div className="h-[395px] md:h-[420px] lg:h-[455px] xl:h-[460px]">
      {/* Image shimmer */}
      <div className="w-full h-[275px] xl:h-[300px] bg-gray-200 shimmer mb-4"></div>

      {/* Title shimmer */}
      <div className="h-5 bg-gray-200 shimmer rounded mb-2 w-3/4"></div>

      {/* Subtitle shimmer */}
      <div className="h-4 bg-gray-200 shimmer rounded mb-2 w-full"></div>
      <div className="h-4 bg-gray-200 shimmer rounded mb-2 w-2/3"></div>

      {/* Description shimmer */}
      <div className="h-4 bg-gray-200 shimmer rounded w-1/2"></div>
    </div>
  );
};

const ArtWorkData = ({ artwork }: { artwork: ICollectionData }) => {

  const displayDates = (date1: string, date2: string) => {
    const date = date2 ? date1 + " - " + date2 : date1;
    return date;
  };
  return (<div
    className="col col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 mb-8 lg:h-[400px] xl:h-[400px]"
  >
    <Card
      id={artwork?.artworkId}
      title={artwork?.artistName}
      subtitle1={artwork?.artworkTitle}
      subtitle1Cls={"truncate-2-lines"}
      description={
        artwork?.isCircaDate
          ? "Circa " + artwork?.dateOfProduction
          : artwork?.isUnknownDate
            ? "Unknown Date"
            : displayDates(
              artwork?.dateOfProduction,
              artwork?.dateOfProductionEnd,
            )
      }
      image={artwork?.imageReference}
      slugUrl={"collection"}
      cls={"h-[275px] xl:h-[300px] object-cover"}
      fullCardCls={
        "h-[395px] md:h-[420px] lg:h-[455px] xl:h-[460px]"
      }
    ></Card>
  </div>
  );
}